import React from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, Outlet, useLocation } from "react-router";
import { Page, Tabs } from "@sscs/ui";
import {
  FORBIDDEN_ROUTE,
  GROUP_MAPPING_ROUTE,
  PERMISSION,
  USER_DIRECTORY_ROUTE,
} from "Constants";
import { useAllowedTo, useCurrentOrganization } from "Hooks";
import { useConfigurationQuery } from "../../services/sso.api";
import { Protocol, UserDirectoryOutletContext } from "../../types";

const GROUP_MAPPING_PROTOCOLS: Protocol[] = ["sso-oidc", "sso-saml"];

const UserDirectoryPage = (): React.JSX.Element | null => {
  const canViewSso = useAllowedTo({
    permissions: [PERMISSION.SETUP_SSO],
  });
  const currentOrganization = useCurrentOrganization();
  const { pathname } = useLocation();

  const { data: ssoConfiguration, isLoading: isSsoConfigLoading } =
    useConfigurationQuery(
      { orgId: currentOrganization?.id || "" },
      { skip: !currentOrganization || !canViewSso },
    );

  const protocolForm = useForm<{ protocol: Protocol }>({
    values: {
      protocol: ssoConfiguration?.user_directory.active ?? "local",
    },
  });
  const selectedProtocol = protocolForm.watch("protocol");

  if (!canViewSso) {
    return <Navigate to={`/${FORBIDDEN_ROUTE}`} replace />;
  }

  const supportsGroupMapping =
    GROUP_MAPPING_PROTOCOLS.includes(selectedProtocol);

  const isOnGroupMapping = pathname.includes(GROUP_MAPPING_ROUTE);

  if (!isSsoConfigLoading && isOnGroupMapping && !supportsGroupMapping) {
    return <Navigate to="." replace />;
  }

  const activeTab = isOnGroupMapping
    ? GROUP_MAPPING_ROUTE
    : USER_DIRECTORY_ROUTE;

  return (
    <Page.Root>
      <Page.Header title="Manage SSO configuration" />
      <Page.Tabs>
        <Tabs.Tabs value={activeTab}>
          <Tabs.Tab
            id="user-directory-tab"
            value={USER_DIRECTORY_ROUTE}
            label="User directory (SSO)"
            to="."
            component={Link}
          />
          {supportsGroupMapping && (
            <Tabs.Tab
              id="group-mapping-tab"
              value={GROUP_MAPPING_ROUTE}
              label="Group mapping"
              to={GROUP_MAPPING_ROUTE}
              component={Link}
            />
          )}
        </Tabs.Tabs>
      </Page.Tabs>
      <Outlet context={{ protocolForm } satisfies UserDirectoryOutletContext} />
    </Page.Root>
  );
};

export default UserDirectoryPage;
